import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import SMSConsentBlock from "../../components/SMSConsentBlock";
import PageMeta from "../../components/seo/PageMeta";
import { SHIFT_CONNECT_LOGO } from "../../components/TritalLogo";
import {
  CEIPAL_SUBMIT_RESUME_URL,
  RECRUITER_PHONE,
  RECRUITER_PHONE_HUMAN,
} from "../../data/recruitingLinks";
import { api } from "../../lib/api";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, ChevronDown, Send, ExternalLink,
  Plane, MapPin, CalendarClock, Dumbbell, Stethoscope, Crown, Video,
  HeartHandshake, ShieldCheck, Calculator, MessageSquare, Gauge, Quote, Sparkles,
  BookOpenCheck, Clock, BadgeCheck, Headphones, Star, Briefcase, UserCheck,
  Microscope, Smartphone,
} from "lucide-react";

const heroImg    = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg";
const supportImg = "https://images.pexels.com/photos/6129494/pexels-photo-6129494.jpeg";

/* --------------------------------------------------------------------------- */
/*  Content                                                                     */
/* --------------------------------------------------------------------------- */

const OPPORTUNITIES = [
  { Icon: Plane,        t: "Travel Assignments",  d: "13-week+ contracts across all 50 states. Pre-cleared credentials and transparent rate structures.", chip: "Travel" },
  { Icon: MapPin,       t: "Local Contracts",     d: "Commute-friendly roles with defined terms — no housing logistics, no tax-home questions.",            chip: "Local" },
  { Icon: CalendarClock,t: "Per Diem Shifts",     d: "Same-week PRN coverage with open-shift marketplace. Accept, decline, or block dates on your terms.",  chip: "PRN" },
  { Icon: Microscope,   t: "Allied Careers",      d: "Lab, imaging, respiratory, cardiac, and surgical tech roles — inpatient and outpatient.",             chip: "Allied" },
  { Icon: Dumbbell,     t: "Therapy Careers",     d: "PT, OT, SLP, COTA, PTA opportunities across inpatient rehab, SNF, home health, and schools.",         chip: "Therapy" },
  { Icon: Stethoscope,  t: "Physician & APP",     d: "MD, DO, NP, and PA placements in hospitalist, specialty, locum, and permanent roles.",                 chip: "Physician / APP" },
  { Icon: Video,        t: "Telehealth",          d: "Virtual-care positions for tele-ICU, behavioral health, urgent care, and specialty consult.",          chip: "Telehealth" },
  { Icon: Crown,        t: "Interim Leadership",  d: "Director and executive placements for clinicians transitioning into leadership engagements.",          chip: "Leadership" },
];

const WHY_CLINICIANS = [
  { Icon: Calculator,    t: "Pay math on the screen",              d: "Bill rate, taxable hourly, housing and meal stipends, overtime, and blended rate — visible before you submit." },
  { Icon: ShieldCheck,   t: "Credentials that travel with you",    d: "Upload documents once. We match them against each facility's requirements without re-submission loops." },
  { Icon: BadgeCheck,    t: "Recruiters who listen first",          d: "You brief your recruiter on pay target, location, shift pattern, and deal-breakers. We come back with roles that actually fit." },
  { Icon: Clock,         t: "No-pressure timelines",                d: "Decline a submission without explanation. Pause your profile for a license cycle. Reactivate whenever you're ready." },
  { Icon: HeartHandshake,t: "Benefits that actually kick in",       d: "Day-one health coverage on travel contracts, 401(k) with match, license reimbursement, and CEU support when your contract qualifies." },
  { Icon: BookOpenCheck, t: "Specialty respect",                    d: "ICU, OR, Cath Lab, ER — matching uses your competency checklist and years-by-specialty, not a generic keyword scan." },
];

const SUPPORT_FEATURES = [
  { t: "One recruiter, one point of contact", d: "Not a rotating call queue. Your recruiter stays with you across assignments unless you request a change." },
  { t: "Text-first communication",             d: "Respond on your schedule. Full conversation history stored in your dashboard." },
  { t: "On-assignment support",                 d: "Housing referrals, licensing help, timesheet corrections, credential renewals — real people, real turnaround." },
  { t: "Transparent reviews",                   d: "Read honest clinician reviews of facilities, including charge-RN patterns, on-call expectations, and orientation length." },
  { t: "Privacy on your terms",                 d: "Your profile is visible only to facilities you approve. Opt in or out of each submission." },
  { t: "Weekly pay, itemized",                  d: "Direct deposit every Friday with full stipend and taxable breakdown — downloadable for your accountant." },
];

const PATHWAYS = [
  { Icon: Plane,         t: "Travel",       length: "13–26 weeks",  pay: "Blended: taxable + non-taxable stipends", best: "Clinicians with tax-home flexibility", href: "/services/travel-nursing" },
  { Icon: MapPin,         t: "Local Contract", length: "4–13 weeks",   pay: "W-2 hourly with overtime",              best: "Clinicians anchored by family or licensing",  href: "/services/local-per-diem-staffing" },
  { Icon: CalendarClock,  t: "Per Diem / PRN", length: "Shift-by-shift", pay: "Hourly W-2 or 1099 by preference",     best: "Supplemental income, schedule control",         href: "/shiftbridge", external: true },
  { Icon: Briefcase,      t: "Contract",       length: "Facility-defined", pay: "Hourly, overtime, optional benefits", best: "Deeper facility integration", href: "/services/contract-direct-hire" },
  { Icon: UserCheck,      t: "Direct Hire",    length: "Permanent",     pay: "Salaried with full benefits",            best: "Career placement with relocation support",     href: "/services/contract-direct-hire" },
];

const PROCESS_STEPS = [
  { n: "01", t: "Create your profile",      d: "Share your specialty, credentials, pay target, and shift preferences. Takes about 8 minutes.",      Icon: Sparkles },
  { n: "02", t: "Complete your checklist",  d: "Specialty-specific skills checklist — self-rate with years-of-experience markers. Travels with you.", Icon: BookOpenCheck },
  { n: "03", t: "Match on your terms",      d: "Review verified assignments that fit your preferences. Submit to the ones you want, skip the rest.", Icon: Gauge },
  { n: "04", t: "Start, get paid, renew",   d: "Clock in through the app, log your hours, receive transparent weekly pay, and roll into your next role.", Icon: HeartHandshake },
];

const FAQS = [
  { q: "Does it cost anything to create a profile?", a: "No. Creating a profile, browsing assignments, and talking with a recruiter is always free for clinicians." },
  { q: "How is my pay calculated on travel contracts?", a: "You'll see a full breakdown before you submit: taxable hourly, weekly housing stipend, weekly meal stipend, overtime multiplier, and blended rate. What's shown is what lands in your bank account." },
  { q: "Do I have to work with just one recruiter?", a: "You have one dedicated recruiter who stays with you across assignments. You can request a different recruiter at any time — no explanation needed." },
  { q: "What credentials do I need to get started?", a: "An active RN/LPN/APP license for your specialty is the minimum. Additional certifications (BLS, ACLS, PALS, specialty-specific) depend on the role. Upload what you have and we'll flag gaps per submission." },
  { q: "Can I pause my profile?", a: "Yes. Pause for a week, a month, or an entire license cycle. Reactivation takes one click. Your documents stay stored and compliant." },
  { q: "What benefits are available?", a: "On qualifying travel contracts: day-one medical, dental, and vision; 401(k) with match; license reimbursement; CEU allowance; and optional life and disability coverage. PRN work is W-2 hourly or 1099 by preference." },
  { q: "How is housing handled on travel assignments?", a: "Take the weekly housing stipend (tax-free if you qualify under IRS tax-home rules) and self-arrange, or request agency-placed housing. We work with national corporate-housing partners." },
  { q: "What if an assignment doesn't work out?", a: "Contracts include mutual early-termination language. You, the facility, and your recruiter review together. We don't blacklist clinicians for documented cause." },
  { q: "Do you work with new graduates?", a: "Yes — for per diem and local roles with appropriate support. Most travel assignments require at least 1–2 years of specialty experience; some specialties require more." },
];

/* --------------------------------------------------------------------------- */
/*  Page                                                                        */
/* --------------------------------------------------------------------------- */

export default function Clinicians() {
  const [faqOpen, setFaqOpen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false); // TCPA: SMS consent
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    specialty: "ICU", license_state: "TX", years: "3+ years",
    interest: "Travel", notes: "",
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const res = await fetch(`/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          source: "clinicians",
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          sms_opt_in: smsOptIn,
          specialty: form.specialty,
          licenseState: form.license_state,
          yearsExp: form.years,
          assignmentType: form.interest,
          notes: form.notes || null,
          idempotencyKey: requestId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error("Submission failed");
      toast.success("Thanks. A recruiter will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", specialty: "ICU", license_state: "TX", years: "3+ years", interest: "Travel", notes: "" });
      setSmsOptIn(false);
    } catch {
      toast.error("We couldn't deliver that message. Please try again or email info@tritalcare.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="clinicians-page">
      <PageMeta
        title="Healthcare Careers for Nurses & Allied Clinicians | TRITAL Care®"
        description="Connect with TRITAL Care recruiters, search healthcare jobs, submit your resume, and explore nursing, allied health, therapy, local, travel, and contract opportunities."
        canonical="https://www.tritalcare.com/clinicians"
      />
      <PublicNav />

      {/* 1 · HERO */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="clinicians-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Stethoscope className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">For Clinicians</span>
            </div>
            <h1 className="mt-6 font-display font-black text-[44px] leading-[1.02] sm:text-5xl lg:text-[72px] lg:leading-[1.02] tracking-tight text-slate-900 text-balance">
              Your career.
              <span className="block text-trital-navy">Your schedule.</span>
              <span className="block text-trital-red">Your numbers.</span>
            </h1>
            <p className="mt-6 text-[16px] lg:text-[17px] text-slate-600 max-w-xl leading-relaxed">
              TRITAL Care® is a clinical workforce platform built around the clinicians who use it. Travel, local, per diem
              and permanent opportunities — with pay math on the screen, credentials that travel with you, and a recruiter
              who listens first.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="clinicians-hero-ctas">
              <Link
                to="/careers/jobs"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep"
                data-testid="clinicians-hero-find-jobs"
              >
                Find Jobs <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
              </Link>
              <a
                href={CEIPAL_SUBMIT_RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep shadow-[0_10px_30px_-10px_rgba(19,39,122,0.45)]"
                data-testid="clinicians-hero-submit-resume"
              >
                Submit Resume <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
              </a>
              <a
                href={`sms:${RECRUITER_PHONE}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 bg-white text-trital-navy font-semibold hover:border-trital-navy hover:bg-slate-50 btn-lift"
                data-testid="clinicians-hero-text-recruiter"
              >
                <MessageSquare className="w-4 h-4" strokeWidth={1.8} /> Text Recruiter {RECRUITER_PHONE_HUMAN}
              </a>
            </div>
            <div className="mt-3 text-[12px] text-slate-500" data-testid="clinicians-hero-checklist-link">
              Already vetted?{" "}
              <Link to="/compliance/checklists" className="text-trital-navy font-semibold underline underline-offset-2 hover:text-trital-red">
                Complete a Skill Checklist
              </Link>
              {" "}— or{" "}
              <Link to="/register?role=clinician" className="text-trital-navy font-semibold underline underline-offset-2 hover:text-trital-red">
                Join our network
              </Link>.
            </div>
            <div className="mt-8 flex items-center gap-5 text-[13px] text-slate-600" data-testid="clinicians-hero-rating">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((i) => <Star key={i} className="w-4 h-4 fill-trital-red text-trital-red" />)}
              </div>
              <span className="text-slate-500">4.8 average from verified clinician reviews</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="lg:col-span-5 relative">
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-navy relative">
              <img src={heroImg} alt="Clinician reviewing care plan at workstation" className="w-full h-[480px] lg:h-[540px] object-cover" style={{ filter: "saturate(0.92) contrast(1.02)" }} />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-trital-navy-ink/70 via-trital-navy-ink/10 to-transparent" />
              <div className="absolute left-5 bottom-5 right-5 glass-card rounded-2xl p-4 bg-white/94">
                <div className="overline text-trital-red">Pay transparency</div>
                <div className="mt-1 font-display font-bold text-slate-900">Your rate is visible before you submit. Always.</div>
              </div>
            </div>
            <div className="absolute -top-4 -right-3 bg-white rounded-2xl shadow-card-hover border border-slate-200 p-4 w-56 hidden sm:block">
              <div className="overline text-trital-navy">Weekly gross</div>
              <div className="font-display font-black text-2xl text-trital-navy mt-1 tabular-nums">$3,284</div>
              <div className="text-[11px] text-slate-500 mt-1">$1,484 taxable + $1,800 stipends</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SMS recruiter access — under hero */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pt-10" data-testid="clinicians-sms-top">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.hero.headline} subtext={SMS_COPY.hero.sub} />
      </section>

      {/* 2 · OPPORTUNITY CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="clinicians-opportunities">
        <div className="max-w-[760px]">
          <div className="overline text-trital-red">Opportunities</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
            Whatever you practice. Wherever you practice it.
          </h2>
          <p className="mt-4 text-slate-600 max-w-[60ch]">
            From 13-week ICU travel contracts to tele-health consults — pick the path that fits your credentials, your
            geography, and your financial goals.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {OPPORTUNITIES.map(({ Icon, t, d, chip }, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.35, delay: (i % 4) * 0.05 }}
              className="card-premium card-premium-hover rounded-2xl p-5"
              data-testid={`opportunity-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                  <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-trital-red font-bold uppercase tracking-wider">{chip}</span>
              </div>
              <div className="mt-4 font-display font-bold text-[17px] text-slate-900">{t}</div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3 · WHY CLINICIANS CHOOSE */}
      <section className="bg-trital-mist border-y border-slate-200" data-testid="clinicians-why">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Why Clinicians Choose TRITAL Care®</div>
              <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
                Built around how clinicians actually want to work.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600">We asked 200 travel and PRN clinicians what they wish an agency would change. Then we shipped it.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CLINICIANS.map(({ Icon, t, d }) => (
              <div key={t} className="card-premium rounded-2xl p-6">
                <div className="w-11 h-11 rounded-xl bg-red-50 text-trital-red flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-5 font-display font-bold text-lg text-slate-900">{t}</div>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · RECRUITER SUPPORT EXPERIENCE */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-start" data-testid="clinicians-support">
        <div className="lg:col-span-5">
          <div className="overline text-trital-red">Support & Recruiter Experience</div>
          <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
            The recruiter you'd want, if you picked.
          </h2>
          <p className="mt-4 text-slate-600 max-w-[52ch]">
            Our recruiting team hires clinicians and staffing operators — people who've worked the floor or the scheduling
            desk. They listen before they send submissions.
          </p>
          <div className="mt-7 rounded-3xl overflow-hidden border border-slate-200 shadow-card relative">
            <img src={supportImg} alt="Clinician support team in conversation" className="w-full h-[280px] object-cover" style={{ filter: "saturate(0.92) contrast(1.02)" }} />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-trital-navy-ink/70 via-transparent to-transparent" />
            <div className="absolute left-5 bottom-5 text-white">
              <div className="overline text-white/70">Recruiter promise</div>
              <div className="mt-1 font-display font-bold text-lg">No cold pitches. No hidden rates. No guilt trips.</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 grid md:grid-cols-2 gap-4">
          {SUPPORT_FEATURES.map(({ t, d }) => (
            <div key={t} className="card-premium rounded-2xl p-5">
              <div className="flex items-center gap-2">
                <Headphones className="w-4 h-4 text-trital-navy" strokeWidth={1.8} />
                <div className="font-display font-bold text-[15px] text-slate-900">{t}</div>
              </div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
          <div className="md:col-span-2 rounded-2xl p-6 bg-trital-navy-ink text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-trital-navy-mesh opacity-70 pointer-events-none" aria-hidden="true" />
            <Quote className="w-6 h-6 text-trital-red relative" strokeWidth={1.8} />
            <blockquote className="relative mt-3 text-white/90 text-[15px] italic leading-relaxed">
              "My recruiter remembers that I'm not open to weekends and that my re-licensing window is August. I never have to re-explain myself."
            </blockquote>
            <div className="relative mt-3 text-[12.5px] text-white/60">— Travel ICU RN · 5 years on platform</div>
          </div>
        </div>
      </section>

      {/* 5 · PATHWAYS (Travel / Local / PRN / Contract / Direct Hire) */}
      <section className="bg-white border-t border-slate-200" data-testid="clinicians-pathways">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="max-w-[760px] mb-12">
            <div className="overline text-trital-red">Your pathways</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
              Travel, local, per diem, contract, direct hire — pick the model that fits your life.
            </h2>
            <p className="mt-4 text-slate-600 max-w-[60ch]">
              You're not locked into one model. Blend travel and per diem, roll from contract to direct hire — whatever
              matches your credentials, career stage, and personal life.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PATHWAYS.map((p, i) => {
              const LinkComp = p.external ? "a" : Link;
              const linkProp = p.external ? { href: p.href, target: "_blank", rel: "noopener noreferrer" } : { to: p.href };
              return (
                <motion.div
                  key={p.t}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                >
                  <LinkComp
                    {...linkProp}
                    className="group block card-premium card-premium-hover rounded-2xl p-6 h-full"
                    data-testid={`pathway-${p.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    aria-label={p.external ? `${p.t} (opens in new tab)` : undefined}
                  >
                    <div className="w-10 h-10 rounded-xl bg-trital-navy text-white flex items-center justify-center">
                      <p.Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
                    </div>
                    <div className="mt-4 font-display font-bold text-[17px] text-slate-900 flex items-center gap-1">
                      {p.t}
                      {p.external ? <ExternalLink className="w-3.5 h-3.5 opacity-50" strokeWidth={1.8} /> :
                        <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-trital-red" strokeWidth={1.8} />}
                    </div>
                    <dl className="mt-4 space-y-2 text-[12.5px]">
                      <div>
                        <dt className="overline text-slate-500">Length</dt>
                        <dd className="mt-0.5 text-slate-800 font-semibold">{p.length}</dd>
                      </div>
                      <div>
                        <dt className="overline text-slate-500">Pay model</dt>
                        <dd className="mt-0.5 text-slate-800 font-semibold">{p.pay}</dd>
                      </div>
                      <div>
                        <dt className="overline text-slate-500">Best for</dt>
                        <dd className="mt-0.5 text-slate-600">{p.best}</dd>
                      </div>
                    </dl>
                  </LinkComp>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 · PRN MARKETPLACE TEASER (TRITAL ShiftBridge™) */}
      <section className="relative overflow-hidden bg-trital-navy-mesh text-white" data-testid="clinicians-prn-teaser">
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.22em] font-bold text-trital-red">
              <span className="relative flex items-center justify-center w-2 h-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-trital-red opacity-70 animate-pulse-red" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-trital-red" />
              </span>
              PRN Marketplace · Launching soon
            </div>
            <h2 className="mt-6 font-display font-black text-4xl lg:text-[52px] leading-[1.04] tracking-tight text-balance">
              <span className="text-trital-red">Pick up</span> a PRN shift this week.
            </h2>
            <p className="mt-5 text-white/80 text-[17px] leading-relaxed max-w-[58ch]">
              <span className="font-semibold text-white">TRITAL ShiftBridge™</span> is our PRN and per-diem shift marketplace —
              browse open shifts at verified facilities near you, accept with one tap, and get paid within days. Launching on{" "}
              <a href="/shiftbridge" target="_blank" rel="noopener noreferrer" className="font-semibold text-white underline-offset-4 hover:underline">
                www.tritalcare.com/shiftbridge
              </a>{" "}
              with iOS and Android apps to follow.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register?role=clinician" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="clinicians-prn-cta-pickup">
                Explore PRN Shifts <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <a href="/shiftbridge" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10" aria-label="Open www.tritalcare.com/shiftbridge (opens in new tab)" data-testid="clinicians-prn-cta-open">
                Open www.tritalcare.com/shiftbridge <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </div>
            <div className="mt-6 flex items-center gap-4 text-[13px] text-white/70">
              <span className="inline-flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" strokeWidth={1.8} /> iOS & Android apps coming</span>
              <Link to="/shift-connect" className="inline-flex items-center gap-1 hover:text-white">
                Learn more → <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} />
              </Link>
            </div>
          </div>
          <div className="lg:justify-self-end">
            <div className="glass-card rounded-3xl p-7 bg-white/94 border-white/70 max-w-sm">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-trital-red mb-4 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" strokeWidth={1.8} /> Platform brand
              </div>
              <img 
                src={SHIFT_CONNECT_LOGO} 
                alt="TRITAL ShiftBridge™ — Connecting Clinicians. Powering Care." 
                className="max-h-20 w-auto object-contain select-none mx-auto" 
                draggable={false} 
              />
              <div className="mt-5 pt-4 border-t border-slate-200 text-[12px] text-slate-600 leading-relaxed">
                TRITAL ShiftBridge™ is a sub-brand of{" "}
                <Link to="/" className="font-semibold text-trital-navy hover:underline underline-offset-4">TRITAL Care®</Link>,
                the operating brand of TOPTAL Care Inc.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7 · SIMPLE 4-STEP PROCESS */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden" data-testid="clinicians-process">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="max-w-[720px]">
            <div className="overline text-white/60">Simple Process</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-white text-balance">
              Four steps. No small print.
            </h2>
            <p className="mt-4 text-white/70 max-w-xl">From profile to first paycheck — here's exactly what happens, in order.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS_STEPS.map(({ n, t, d, Icon }, i) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white text-trital-navy font-display font-black text-lg tabular-nums flex items-center justify-center">{n}</div>
                  <Icon className="w-5 h-5 text-trital-red" strokeWidth={1.8} />
                </div>
                <div className="mt-5 font-display font-bold text-lg">{t}</div>
                <p className="mt-2 text-[13.5px] text-white/75 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register?role=clinician" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-trital-navy font-semibold btn-lift hover:bg-slate-100" data-testid="clinicians-process-cta-register">
              Create your profile <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to="/compliance/checklists" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10" data-testid="clinicians-process-cta-checklist">
              Start a skills checklist
            </Link>
          </div>
        </div>
      </section>

      {/* 8 · FAQs */}
      <section className="bg-trital-mist border-y border-slate-200" data-testid="clinicians-faqs">
        <div className="max-w-[880px] mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="text-center max-w-[640px] mx-auto">
            <div className="overline text-trital-red">FAQs</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-[44px] leading-[1.08] text-slate-900 text-balance">
              Honest answers to the questions clinicians actually ask.
            </h2>
          </div>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div key={f.q} className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden" data-testid={`clinician-faq-${i}`}>
                  <button onClick={() => setFaqOpen(open ? null : i)} className="w-full text-left px-6 py-5 flex items-center justify-between gap-4" aria-expanded={open}>
                    <span className="font-display font-bold text-[16px] text-slate-900">{f.q}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 text-trital-navy transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.8} />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} className="overflow-hidden">
                        <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 9 · JOIN NETWORK FORM */}
      <section id="interest" className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 scroll-mt-24" data-testid="clinicians-form-section">
        <div className="rounded-3xl bg-white border border-slate-200 shadow-card p-8 lg:p-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-100">
              <Sparkles className="w-3 h-3 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-red">Join our network</span>
            </div>
            <h2 className="mt-5 font-display font-black text-3xl lg:text-[40px] tracking-tight text-slate-900 text-balance leading-[1.08]">
              A recruiter will reach out within one business day.
            </h2>
            <p className="mt-4 text-slate-600 max-w-md">
              Not ready to create a full profile? Share the basics and we'll send two or three well-matched opportunities
              to review — no spam, no call blitz.
            </p>
            <ul className="mt-6 space-y-2 text-[13.5px] text-slate-700">
              {["You choose the contact channel (text or email)", "You see the rate and location before any submission", "You can unsubscribe or pause contact anytime"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-trital-navy" strokeWidth={1.8} /> {t}</li>
              ))}
            </ul>
            <div className="mt-8 rounded-2xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-trital-navy mt-0.5" strokeWidth={1.8} />
              <div>
                <div className="font-display font-bold text-sm text-slate-900">Prefer to skip the form?</div>
                <div className="text-[13px] text-slate-600">Call us at <a href="tel:+18323036622" className="font-semibold text-trital-navy hover:underline underline-offset-4">+1 (832) 303-6622</a> — a recruiter will answer directly during business hours.</div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 bg-white rounded-2xl" data-testid="clinician-interest-form">
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Full name" value={form.name} onChange={upd("name")} required testid="ci-name" />
              <Field label="Work email" type="email" value={form.email} onChange={upd("email")} required testid="ci-email" />
              <Field label="Mobile (optional)" value={form.phone} onChange={upd("phone")} testid="ci-phone" placeholder="For text-first communication" />
              <div>
                <Label>Primary specialty</Label>
                <select value={form.specialty} onChange={upd("specialty")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="ci-specialty">
                  {["ICU","ER","Med-Surg","OR","Cath Lab","L&D","Telemetry","Oncology","PACU","Respiratory Therapy","Physical Therapy","Occupational Therapy","SLP","Radiology","Lab","APP / NP / PA","Other"].map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <Label>License state</Label>
                <select value={form.license_state} onChange={upd("license_state")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="ci-state">
                  {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Years of experience</Label>
                <select value={form.years} onChange={upd("years")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="ci-years">
                  {["< 1 year","1–2 years","3+ years","5+ years","10+ years"].map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>I'm interested in</Label>
                <select value={form.interest} onChange={upd("interest")} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="ci-interest">
                  {["Travel","Local contracts","Per Diem / PRN","Permanent placement","Interim Leadership","Just exploring"].map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Anything specific? (optional)</Label>
                <textarea rows={4} value={form.notes} onChange={upd("notes")} placeholder="Pay target, preferred cities, shift pattern, dates you're available, deal-breakers…" className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid="ci-notes" />
              </div>
            </div>
            
            {/* TCPA: SMS Consent Block — ALWAYS visible (SimpleTexting requirement) */}
            <div className="mt-5">
              <SMSConsentBlock
                checked={smsOptIn}
                onCheckedChange={setSmsOptIn}
                checkboxId="ci-sms-consent"
              />
            </div>
            
            <button type="submit" disabled={submitting} className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-60 disabled:cursor-not-allowed" data-testid="ci-submit">
              <Send className="w-4 h-4" strokeWidth={1.8} /> {submitting ? "Sending…" : "Send — a recruiter will reach out"}
            </button>
            <div className="mt-3 text-[11px] text-slate-500 text-center">We respect your time. One response, then silence unless you want otherwise.</div>
          </form>
        </div>
      </section>

      {/* 10 · FINAL CTA BAND */}
      <section className="relative overflow-hidden bg-trital-navy-mesh text-white" data-testid="clinicians-cta-band">
        <div className="absolute inset-0 dot-grid opacity-10" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-[1.3fr_1fr] items-center gap-10">
          <div>
            <div className="overline text-trital-red">Ready when you are</div>
            <h3 className="mt-3 font-display font-black text-4xl lg:text-[56px] leading-[1.04] text-balance">
              Your next role, on <span className="text-trital-red">your terms</span>.
            </h3>
            <p className="mt-5 text-white/80 max-w-[58ch]">
              Create a profile in eight minutes. Talk to a real recruiter. Pick up a shift this week. All four paths open
              — no exclusivity, no cold pitches, no guilt trips.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[320px]">
            <a href="#interest" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-slate-900 text-sm font-semibold btn-lift hover:bg-slate-100" data-testid="clinicians-final-cta-find">
              Find Opportunities <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </a>
            <Link to="/register?role=clinician" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-trital-red text-white text-sm font-semibold btn-lift hover:bg-trital-red-deep" data-testid="clinicians-final-cta-join">
              Join Our Network <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <a href="/shiftbridge" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-white/25 text-white text-sm font-semibold hover:bg-white/10" aria-label="Explore PRN Shifts (opens in new tab)" data-testid="clinicians-final-cta-prn">
              Explore PRN Shifts <ExternalLink className="w-4 h-4" strokeWidth={1.8} />
            </a>
            <Link to="/compliance/checklists" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-white/80 text-sm font-semibold hover:text-white" data-testid="clinicians-final-cta-checklist">
              Complete Skill Checklists →
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="clinicians-sms-bottom">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.jobs.headline} subtext={SMS_COPY.jobs.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}
function Label({ children }) {
  return <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-600">{children}</label>;
}
function Field({ label, type = "text", value, onChange, required, testid, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-trital-navy/30 focus:border-trital-navy outline-none" data-testid={testid} />
    </div>
  );
}
